import { LoadingButton } from '@mui/lab';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  Dialog,
  DialogTitle,
  Button,
  Typography,
  Card,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DISCONNECT_XERO_URL,
  GET_INTEGRATIONS_URL,
  GET_XERO_AUTH_URL,
} from 'api/apiConstants';
import Label from 'components/Label';
import PartialPageSpinner from 'components/PartialPageSpinner';
import usePortalId from 'hooks/portal/usePortalId';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';

type Xerotype = {
  _id: string;
  portal_id: string;
  integration_name: string;
  active: boolean;
};

function AccountIntegrations() {
  const theme = useTheme();
  const portalId = usePortalId();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [xeroDetails, setXeroDetails] = useState<Xerotype>();
  const [xeroAccountActive, setXeroAccountActive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [disconnecting, setDisconnecting] = useState<boolean>(false);

  // initiating the oauth process
  const authenticateXero = async () => {
    try {
      const result = await axiosInstance.get<{
        success: boolean;
        authUrl: string;
      }>(`${GET_XERO_AUTH_URL}?portal_id=${portalId}`);
      if (result.data.success) {
        window.location.href = `${result.data.authUrl}`;
      }
    } catch (err) {
      enqueueSnackbar(err, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    const fetchIntegrations = async () => {
      setLoading(true);
      try {
        const result = await axiosInstance.post(`${GET_INTEGRATIONS_URL}`, {
          portal_id: portalId,
        });
        if (result.data.success) {
          setXeroDetails(result.data.data[0]);
          setXeroAccountActive(result.data.data[0].active);
        }
      } catch (err) {
        enqueueSnackbar(err, {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchIntegrations();
    const successSearchParam = searchParams.get('success');
    if (window.location.href.indexOf('success') > -1) {
      if (successSearchParam === 'true') {
        enqueueSnackbar(t('account_connected'), {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(t('error_occurred'), { variant: 'error' });
      }
    }
  }, [enqueueSnackbar]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // disconnecting Xero
  const disconnectXero = async () => {
    setDisconnecting(true);
    try {
      const result = await axiosInstance.post<{
        success: boolean;
        message: string;
        code: string;
      }>(`${DISCONNECT_XERO_URL}`, {
        portal_id: portalId,
        integration_id: xeroDetails?._id,
      });
      if (result.data.success) {
        if (result.data.message === 'Integration successfully disconnected') {
          setXeroAccountActive(false);
        }
        handleClose();
        setSearchParams('');
        enqueueSnackbar(t('acc_disconnected'), {
          variant: 'success',
        });
      }
    } catch (err) {
      enqueueSnackbar(err, {
        variant: 'error',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      {loading ? (
        <PartialPageSpinner />
      ) : (
        <>
          <Card className='p-6 flex flex-col w-[30%] gap-3'>
            <div className='flex justify-end'>
              <Label
                variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                color={xeroAccountActive ? 'success' : 'default'}
              >
                {xeroAccountActive ? t('active') : t('inActive')}
              </Label>
            </div>

            <div className='flex justify-between items-center flex-col gap-3'>
              <img
                src='https://edge.xero.com/images/1.0.0/logo/xero-logo.svg'
                alt='Xero'
                className='w-[30%]'
              />
              <Typography className='text-sm'>
                {xeroAccountActive
                  ? t('connected_to_xero')
                  : t('connect_Xero_single_click')}
              </Typography>
              {xeroAccountActive ? (
                <Button
                  variant='contained'
                  className='mt-3 normal-case'
                  fullWidth
                  onClick={handleClickOpen}
                  size='medium'
                  color='error'
                >
                  {t('disconnect_xero')}
                </Button>
              ) : (
                <Button
                  variant='contained'
                  className='mt-3 normal-case'
                  fullWidth
                  onClick={authenticateXero}
                  size='medium'
                >
                  {t('connect_Xero')}
                </Button>
              )}
            </div>
          </Card>
        </>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>{t('sure_disc_xero')}</DialogTitle>
        <DialogContent>
          <DialogContentText className='mt-3'>
            {t('disconnect_xero_confirmation_notice')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('cancel')}</Button>
          <LoadingButton
            onClick={disconnectXero}
            loading={disconnecting ? true : false}
          >
            {t('disconnect')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AccountIntegrations;
